import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { showConfirm } from '../utils/confirmUtils';
import { Button, Badge } from '../components/ui';

export default function AdminTenants() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingKey, setUpdatingKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await api.get('/admin/tenants');
        setTenants(res.data || []);
      } catch (err) {
        console.error('Erreur fetch tenants:', err);
        setError('Erreur lors du chargement des cabinets');
      } finally {
        setLoading(false);
      }
    };
    if (isSuperAdmin) fetchTenants();
    else setLoading(false);
  }, [isSuperAdmin]);

  const toggleStatus = async (tenant) => {
    const newStatus = tenant.status === 'active' ? 'suspended' : 'active';
    const key = `status:${tenant._id}`;
    setUpdatingKey(key);
    setError(null);
    try {
      const res = await api.patch(`/admin/tenants/${tenant._id}/status`, { status: newStatus });
      const updated = res.data;
      setTenants((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
    } catch (err) {
      console.error('Erreur update tenant status:', err);
      setError("Impossible de mettre à jour le statut du cabinet");
    } finally {
      setUpdatingKey(null);
    }
  };

  const toggleTenantFeature = async (tenant, feature, label) => {
    const current = !!tenant?.entitlements?.[feature];
    const newVal = !current;
    const key = `${feature}:${tenant._id}`;
    setUpdatingKey(key);
    setError(null);
    try {
      const res = await api.patch(`/admin/tenants/${tenant._id}/entitlements`, { [feature]: newVal });
      const updated = res.data;
      setTenants((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
    } catch (err) {
      console.error(`Erreur update ${feature}:`, err);
      setError(`Impossible de mettre à jour l'activation ${label}`);
    } finally {
      setUpdatingKey(null);
    }
  };

  const isTrialExpiredTenant = (tenant) => {
    if (tenant.accountType !== 'TRIAL' || !tenant.trialExpiresAt) return false;
    return new Date(tenant.trialExpiresAt) < new Date();
  };

  const activateAfterTrial = async (tenant) => {
    const key = `activate:${tenant._id}`;
    setUpdatingKey(key);
    setError(null);
    try {
      const res = await api.post(`/admin/tenants/${tenant._id}/activate-after-trial`);
      const updated = res.data;
      setTenants((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
    } catch (err) {
      console.error('Erreur activate after trial:', err);
      setError("Impossible d'activer ce tenant après l'essai.");
    } finally {
      setUpdatingKey(null);
    }
  };

  const deleteTenant = async (tenant) => {
    if (!await showConfirm(`⚠️ Supprimer définitivement le cabinet "${tenant.name}" et TOUTES ses données ?\nCette action est IRRÉVERSIBLE.`)) return;
    const key = `delete:${tenant._id}`;
    setUpdatingKey(key);
    setError(null);
    try {
      await api.delete(`/admin/tenants/${tenant._id}`);
      setTenants(prev => prev.filter(t => t._id !== tenant._id));
    } catch (err) {
      console.error('Erreur suppression tenant:', err);
      setError("Impossible de supprimer ce cabinet");
    } finally {
      setUpdatingKey(null);
    }
  };

  const getTrialInfo = (tenant) => {
    if (tenant.accountType !== 'TRIAL' || !tenant.trialExpiresAt) return null;
    const now = new Date();
    const expiry = new Date(tenant.trialExpiresAt);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: 'Essai Expiré', color: 'bg-red-600 text-white' };
    return { label: `Essai (${diffDays}j)`, color: 'bg-purple-100 text-purple-800 border border-purple-200' };
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white p-6 rounded shadow">
        <h1 className="text-xl font-semibold mb-2">Accès refusé</h1>
        <p className="text-gray-600">Cette section est réservée au super administrateur de la plateforme.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white p-6 rounded shadow">
        <p className="text-gray-600">Chargement des cabinets...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">Tenants inscrits sur la plateforme</h1>
      <p className="text-gray-600 mb-4">
        <strong>Entreprises</strong> : tous les modules se gèrent au niveau du tenant. &nbsp;
        <strong>Cabinet comptable</strong> : Comptabilité, Stock, Signature et Gestion Cabinet gérés par dossier client. &nbsp;
        <strong>Cabinet médical</strong> : comptabilité au niveau tenant, signature électronique obligatoire, pas de stock ni gestion cabinet. Facturation via notes d'honoraires.
      </p>

      {error && <div className="mb-4 p-3 rounded bg-red-100 text-red-800 text-sm">{error}</div>}

      <div className="bg-white shadow rounded overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Nom</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Type</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Pays</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Statut</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Plan</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Comptabilité</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Stock</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Sign. Élec.</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Gestion Cabinet</th>
              <th className="px-4 py-2 text-center font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-4 text-center text-gray-500">Aucun cabinet enregistré pour le moment.</td>
              </tr>
            )}
            {tenants.map((tenant) => {
              const isCabinet = tenant.type === 'cabinet';
              const isMedical = tenant.type === 'cabinet_medical';
              const isCompany = tenant.type === 'company';
              const trialInfo = getTrialInfo(tenant);
              const acctEnabled = !!tenant?.entitlements?.accountingEnabled;
              const stockEnabled = !!tenant?.entitlements?.stockEnabled;
              const esigEnabled = !!tenant?.entitlements?.eSignatureEnabled;
              const cabEnabled = !!tenant?.entitlements?.cabinetManagementEnabled;
              const isUpdatingStatus = updatingKey === `status:${tenant._id}`;
              const isUpdatingAcct = updatingKey === `accountingEnabled:${tenant._id}`;
              const isUpdatingStock = updatingKey === `stockEnabled:${tenant._id}`;
              const isUpdatingEsig = updatingKey === `eSignatureEnabled:${tenant._id}`;
              const isUpdatingCab = updatingKey === `cabinetManagementEnabled:${tenant._id}`;
              const isUpdatingActivate = updatingKey === `activate:${tenant._id}`;
              const canActivateAfterTrial = isTrialExpiredTenant(tenant);

              const typeLabel = isCabinet ? 'Cabinet comptable'
                : isMedical ? 'Cabinet médical'
                : 'Entreprise';

              return (
                <tr key={tenant._id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{tenant.name}</div>
                    <div className="text-xs text-gray-500">ID: {tenant._id}</div>
                  </td>
                  <td className="px-4 py-2 capitalize">
                    <span className={`font-medium text-xs px-2 py-0.5 rounded ${
                      isCabinet ? 'bg-blue-100 text-blue-800'
                        : isMedical ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {typeLabel}
                    </span>
                    {isCabinet && (
                      <span className="ml-1 text-xs text-gray-500">(dossiers)</span>
                    )}
                  </td>
                  <td className="px-4 py-2">{tenant.country || 'N/A'}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-col items-start gap-1">
                      <Badge variant={tenant.status === 'active' ? 'success' : tenant.status === 'suspended' ? 'danger' : 'warning'}>
                        {tenant.status === 'active' ? 'Actif' : tenant.status === 'suspended' ? 'Suspendu' : 'En attente'}
                      </Badge>
                      {trialInfo && (
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${trialInfo.color}`}>{trialInfo.label}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    {tenant.subscription?.plan || <span className="text-gray-400 text-xs">Aucun plan défini</span>}
                  </td>

                  {/* Comptabilité */}
                  <td className="px-4 py-2">
                    {isCabinet ? (
                      <span className="text-gray-400 text-xs italic">Par dossier</span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Badge variant={acctEnabled ? 'success' : 'gray'}>{acctEnabled ? 'Activée' : 'Désactivée'}</Badge>
                        <Button variant={acctEnabled ? 'warning' : 'success'} size="sm"
                          onClick={() => toggleTenantFeature(tenant, 'accountingEnabled', 'Comptabilité')}
                          disabled={isUpdatingAcct}>
                          {isUpdatingAcct ? '...' : acctEnabled ? 'Désactiver' : 'Activer'}
                        </Button>
                      </div>
                    )}
                  </td>

                  {/* Stock */}
                  <td className="px-4 py-2">
                    {isMedical ? (
                      <span className="text-gray-400 text-xs italic">N/A</span>
                    ) : isCabinet ? (
                      <span className="text-gray-400 text-xs italic">Par dossier</span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Badge variant={stockEnabled ? 'success' : 'gray'}>{stockEnabled ? 'Activé' : 'Désactivé'}</Badge>
                        <Button variant={stockEnabled ? 'warning' : 'success'} size="sm"
                          onClick={() => toggleTenantFeature(tenant, 'stockEnabled', 'Stock')}
                          disabled={isUpdatingStock}>
                          {isUpdatingStock ? '...' : stockEnabled ? 'Désactiver' : 'Activer'}
                        </Button>
                      </div>
                    )}
                  </td>

                  {/* Signature électronique */}
                  <td className="px-4 py-2">
                    {isMedical ? (
                      <Badge variant="success">Activée (Obligatoire)</Badge>
                    ) : isCabinet ? (
                      <span className="text-gray-400 text-xs italic">Par dossier</span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Badge variant={esigEnabled ? 'success' : 'gray'}>{esigEnabled ? 'Activée' : 'Désactivée'}</Badge>
                        <Button variant={esigEnabled ? 'warning' : 'success'} size="sm"
                          onClick={() => toggleTenantFeature(tenant, 'eSignatureEnabled', 'Signature électronique')}
                          disabled={isUpdatingEsig}>
                          {isUpdatingEsig ? '...' : esigEnabled ? 'Désactiver' : 'Activer'}
                        </Button>
                      </div>
                    )}
                  </td>

                  {/* Gestion Cabinet */}
                  <td className="px-4 py-2">
                    {isMedical ? (
                      <span className="text-gray-400 text-xs italic">N/A</span>
                    ) : isCabinet ? (
                      <span className="text-gray-400 text-xs italic">Par dossier</span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Badge variant={cabEnabled ? 'success' : 'gray'}>{cabEnabled ? 'Activée' : 'Désactivée'}</Badge>
                        <Button variant={cabEnabled ? 'warning' : 'success'} size="sm"
                          onClick={() => toggleTenantFeature(tenant, 'cabinetManagementEnabled', 'Gestion Cabinet')}
                          disabled={updatingKey === `cabinetManagementEnabled:${tenant._id}`}>
                          {updatingKey === `cabinetManagementEnabled:${tenant._id}` ? '...' : cabEnabled ? 'Désactiver' : 'Activer'}
                        </Button>
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {isCabinet && (
                        <Button variant="primary" size="sm"
                          onClick={() => navigate(`/admin/tenants/${tenant._id}/workspaces`)}
                          title="Gérer les modules par dossier">
                          Dossiers
                        </Button>
                      )}
                      {canActivateAfterTrial && (
                        <Button variant="primary" size="sm" onClick={() => activateAfterTrial(tenant)} disabled={isUpdatingActivate}>
                          {isUpdatingActivate ? '...' : 'Activer essai'}
                        </Button>
                      )}
                      <Button variant={tenant.status === 'active' ? 'danger' : 'success'} size="sm"
                        onClick={() => toggleStatus(tenant)} disabled={isUpdatingStatus}>
                        {isUpdatingStatus ? '...' : tenant.status === 'active' ? 'Suspendre' : 'Activer'}
                      </Button>
                      <Button variant="danger" size="sm"
                        onClick={() => deleteTenant(tenant)}
                        disabled={updatingKey === `delete:${tenant._id}`}>
                        {updatingKey === `delete:${tenant._id}` ? '...' : 'Supprimer'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Note: Le bouton "Dossiers" est disponible uniquement pour les cabinets comptables (gestion des modules par client). Les cabinets médicaux fonctionnent comme des entreprises avec signature électronique obligatoire et facturation via notes d'honoraires.
      </div>
    </div>
  );
}
